/**
 * Vigencia de una membresía: desde una fecha, hasta N meses después
 * menos 1 día — con el overflow de mes bien resuelto.
 *
 * Extraído de `RenovarMembresia.tsx` (donde nació con el Fix 2 MEDIUM de
 * la Task 9) porque `Membresia.tsx` reimplementaba la misma cuenta con
 * el defecto original (`setMonth(+n)` + `setDate(-1)`): 31-ago + 6 meses
 * "cae" en 31-feb, que no existe, y `Date.setMonth` rueda al 2/3-mar en
 * silencio (fix final F6). Una sola implementación, dos consumidores:
 *
 * - `RenovarMembresia` — "Vigencia nueva", vía `planDeRenovacion`.
 * - `Membresia` — hasta cuándo está pagado (`tier_valid_until` del
 *   contexto, issue #734).
 *
 * Reglas (aritmética de calendario, no de milisegundos):
 *
 * - Si el día de `desde` SÍ existe en el mes destino (caso normal, ej.
 *   1-sep + 12 meses -> 1-sep del año siguiente), se resta 1 día ->
 *   31-ago.
 * - Si NO existe (overflow, ej. 31-ago + 6 meses -> "31-feb"), la
 *   vigencia termina el último día real de ese mes (28-feb o 29-feb
 *   según el año) — sin restar 1 día aparte, porque ese último día YA
 *   es el límite (no hay "31-feb" del que restar).
 *
 * `new Date(anio, mes, 0)` (día 0) es el truco estándar para "último día
 * del mes anterior a `mes`" — por eso `mesDestino + 1` da el último día
 * de `mesDestino`, y pasar `dia - 1` directo como día (incluido
 * `dia === 1` -> día 0) resuelve el caso normal sin un `.setDate()`
 * aparte.
 */

import type { MembresiaPeriod } from "@/lib/api";

/**
 * Duración en meses de cada período del catálogo. `monthly` no entra:
 * es legacy solo-lectura desde la decisión del founder del 1-sep-2026
 * (ningún flujo nuevo puede asignarlo) — quien necesite leerlo de un
 * tenant viejo lo agrega en su propio mapa (ver `Membresia.tsx`).
 */
export const MESES_POR_PERIODO: Record<MembresiaPeriod, number> = {
  trimestral: 3,
  semestral: 6,
  annual: 12,
};

export interface Vigencia {
  desde: Date;
  hasta: Date;
}

/** Ver docstring del módulo. `desde` se copia — nunca se muta la fecha de entrada. */
export function calcularVigencia(desde: Date, meses: number): Vigencia {
  const anio = desde.getFullYear();
  const mesDestino = desde.getMonth() + meses;
  const dia = desde.getDate();
  const ultimoDiaMesDestino = new Date(anio, mesDestino + 1, 0).getDate();

  const hasta =
    dia <= ultimoDiaMesDestino
      ? new Date(anio, mesDestino, dia - 1)
      : new Date(anio, mesDestino, ultimoDiaMesDestino);

  return { desde: new Date(desde), hasta };
}

/** `01 de sept de 2026` — el formato corto es-CO con el que las dos páginas pintan la vigencia. */
export function formatoFecha(d: Date): string {
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

// --- Días de calendario (issue #734) ----------------------------------------

/**
 * `"2027-03-31"` -> 31-mar-2027 a medianoche LOCAL. `new Date("2027-03-31")`
 * la leería como medianoche UTC, que en Colombia todavía es el 30.
 */
export function fechaDesdeIso(iso: string): Date {
  const [anio = Number.NaN, mes = Number.NaN, dia = Number.NaN] = iso.split("-").map(Number);
  return new Date(anio, mes - 1, dia);
}

/** El mismo día a medianoche local: descarta la hora. */
function soloDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function sumarDias(d: Date, dias: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dias);
}

/** Días de calendario de `desde` a `hasta` (negativo si `hasta` es antes). La hora no cuenta. */
export function diasEntre(desde: Date, hasta: Date): number {
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utc(hasta) - utc(desde)) / 86_400_000);
}

/**
 * Primer día del período de `meses` que termina en `hasta`: la cuenta
 * inversa de `calcularVigencia`. Si el día no existe en el mes de inicio,
 * se toma el último de ese mes (solo pasa tras un overflow, y la
 * diferencia es de uno a tres días).
 */
export function inicioDelPeriodo(hasta: Date, meses: number): Date {
  const siguiente = sumarDias(hasta, 1);
  const anio = siguiente.getFullYear();
  const mes = siguiente.getMonth() - meses;
  const ultimoDelMes = new Date(anio, mes + 1, 0).getDate();
  return new Date(anio, mes, Math.min(siguiente.getDate(), ultimoDelMes));
}

// --- Qué cubre un pago (issue #734) -----------------------------------------

/**
 * Lo que pasaría al pagar `tier`/`period` hoy. Mismas reglas que
 * `backend/subsystems/municipal_portal/vigencia.py`, que es quien decide al
 * cobrar; esto es lo que la página muestra antes de pagar:
 *
 * - `activacion`: sin período vigente, lo nuevo arranca hoy.
 * - `renovacion`: mismo plan con el período vigente; lo nuevo empieza el
 *   día siguiente al vencimiento (no se pierden días).
 * - `cambio_a_pro`: de Esencial a Pro con el período vigente; aplica hoy y
 *   no mueve el vencimiento. Se cobra la diferencia por los días que
 *   quedan (`cobroCambioAPro` en `membresia-precios.ts`).
 * - `cambio_a_esencial_bloqueado`: de Pro a Esencial solo al vencer.
 */
export type PlanDeRenovacion =
  | { tipo: "activacion"; desde: Date; hasta: Date }
  | { tipo: "renovacion"; vigenteHasta: Date; desde: Date; hasta: Date }
  | {
      tipo: "cambio_a_pro";
      vigenteHasta: Date;
      /** Período con el que se pagó, o `null` si no tiene precio (legacy `monthly`). */
      periodoPagado: MembresiaPeriod | null;
      /** Días que quedan, hoy incluido. */
      diasRestantes: number;
      /** Días del período pagado que termina en `vigenteHasta`. */
      diasDelPeriodo: number;
    }
  | { tipo: "cambio_a_esencial_bloqueado"; vigenteHasta: Date };

function esPeriodoDelCatalogo(periodo: string | null): periodo is MembresiaPeriod {
  return periodo === "trimestral" || periodo === "semestral" || periodo === "annual";
}

export function planDeRenovacion({
  hoy,
  tierActual,
  periodoActual,
  vigenteHasta,
  tier,
  period,
}: {
  hoy: Date;
  tierActual: string | null;
  periodoActual: string | null;
  /** Último día pagado (`tier_valid_until`), o `null` si no hay registro. */
  vigenteHasta: Date | null;
  tier: string;
  period: MembresiaPeriod;
}): PlanDeRenovacion {
  const dia = soloDia(hoy);

  if (vigenteHasta === null || diasEntre(dia, vigenteHasta) < 0) {
    return { tipo: "activacion", ...calcularVigencia(dia, MESES_POR_PERIODO[period]) };
  }

  if (tier === tierActual) {
    const desde = sumarDias(vigenteHasta, 1);
    return {
      tipo: "renovacion",
      vigenteHasta,
      desde,
      hasta: calcularVigencia(desde, MESES_POR_PERIODO[period]).hasta,
    };
  }

  if (tier === "pro") {
    const periodoPagado = esPeriodoDelCatalogo(periodoActual) ? periodoActual : null;
    const meses = MESES_POR_PERIODO[periodoPagado ?? "annual"];
    return {
      tipo: "cambio_a_pro",
      vigenteHasta,
      periodoPagado,
      diasRestantes: diasEntre(dia, vigenteHasta) + 1,
      diasDelPeriodo: diasEntre(inicioDelPeriodo(vigenteHasta, meses), vigenteHasta) + 1,
    };
  }

  return { tipo: "cambio_a_esencial_bloqueado", vigenteHasta };
}
